/**
 * Diálogo de configurações: base URL da API, API key (fornecida pelo usuário —
 * AD-7) e plano usado na validação local (R19.5).
 *
 * Acessibilidade (R19.7): é um diálogo modal com `role="dialog"`,
 * `aria-modal`, título associado por `aria-labelledby`, foco inicial no primeiro
 * campo, fechamento por Esc e rótulos explícitos em todos os campos.
 */
import { useEffect, useId, useRef, useState } from "react";

import { TIER_CATALOG, TIERS, type Tier } from "../config/tiers";
import { formatBytes } from "../lib/validation";
import type { Settings } from "../lib/settings";
import { Button } from "./Button";

interface SettingsDialogProps {
  readonly initial: Settings;
  readonly onSave: (settings: Settings) => void;
  readonly onClose: () => void;
  /** Se `true`, é a primeira configuração (não permite fechar sem salvar). */
  readonly required?: boolean;
}

export function SettingsDialog({ initial, onSave, onClose, required = false }: SettingsDialogProps) {
  const titleId = useId();
  const keyId = useId();
  const urlId = useId();
  const tierId = useId();
  const keyHintId = useId();

  const [apiKey, setApiKey] = useState(initial.apiKey);
  const [apiBaseUrl, setApiBaseUrl] = useState(initial.apiBaseUrl);
  const [tier, setTier] = useState<Tier>(initial.tier);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !required) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, required]);

  const canSave = apiKey.trim() !== "" && apiBaseUrl.trim() !== "";

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) {
      return;
    }
    onSave({ apiKey: apiKey.trim(), apiBaseUrl: apiBaseUrl.trim(), tier });
  };

  return (
    <div
      className="mp-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !required) {
          onClose();
        }
      }}
    >
      <div className="mp-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 className="mp-modal__title" id={titleId}>
          Configurações
        </h2>
        <p className="mp-modal__intro">
          Informe o endereço do serviço e a sua chave de acesso. A chave fica salva apenas
          neste navegador e é usada só para falar com o MakePub.
        </p>
        <form onSubmit={submit} noValidate>
          <div className="mp-field">
            <label className="mp-field__label" htmlFor={keyId}>
              Chave de acesso (API key)
            </label>
            <input
              ref={firstFieldRef}
              id={keyId}
              className="mp-input"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              aria-describedby={keyHintId}
              required
            />
            <p className="mp-field__hint" id={keyHintId}>
              Fornecida pela equipe do MakePub. Guardada apenas no seu navegador.
            </p>
          </div>

          <div className="mp-field">
            <label className="mp-field__label" htmlFor={urlId}>
              Endereço do serviço
            </label>
            <input
              id={urlId}
              className="mp-input"
              type="url"
              inputMode="url"
              value={apiBaseUrl}
              onChange={(e) => setApiBaseUrl(e.target.value)}
              placeholder="https://makepub.wecando.click"
              required
            />
          </div>

          <div className="mp-field">
            <label className="mp-field__label" htmlFor={tierId}>
              Seu plano
            </label>
            <select
              id={tierId}
              className="mp-select"
              value={tier}
              onChange={(e) => setTier(e.target.value as Tier)}
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {TIER_CATALOG[t].label} — até {formatBytes(TIER_CATALOG[t].maxFileBytes)}
                </option>
              ))}
            </select>
            <p className="mp-field__hint">
              Usado para avisar antes do envio se o arquivo excede o limite do seu plano.
            </p>
          </div>

          <div className="mp-actions">
            <Button type="submit" variant="primary" disabled={!canSave}>
              Salvar
            </Button>
            {required ? null : (
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
